/**
 * Typed-deal error code → user-facing copy.
 *
 * Backend (`backend/app/modules/deals/typed/exceptions.py`) emits a structured
 * error envelope on every typed-deal failure path:
 *
 *     { detail: { error: { code, message, field, received } } }
 *
 * The raw `message` is engineer-friendly ("Invalid ISO 4217 currency code: 'INRR'");
 * sellers need actionable copy. This module is the single source of truth for
 * that translation. Keep these strings short, action-oriented, and keep the
 * `received` value visible so the seller can see what they typed.
 *
 * Adding a new error code? Add it to `TypedDealErrorCode` AND to
 * `TYPED_DEAL_ERROR_COPY` so the type-checker forces you to ship copy.
 */

export type TypedDealErrorCode =
  | "category_lookup_miss"
  | "subcategory_parent_mismatch"
  | "category_item_type_mismatch"
  | "routing_mismatch"
  | "discriminator_mismatch"
  | "service_block_mismatch"
  | "invalid_currency"
  | "cross_group_recategorisation_required"
  | "promotional_category_as_primary"
  | "likes_not_client_settable"
  | "not_found"
  | "forbidden";

export interface TypedDealErrorPayload {
  code: TypedDealErrorCode | string;
  message?: string;
  field?: string | null;
  received?: string | null;
}

const TYPED_DEAL_ERROR_COPY: Record<TypedDealErrorCode, (p: TypedDealErrorPayload) => string> = {
  category_lookup_miss: (p) =>
    `We don't have a category called "${p.received ?? ""}" anymore. Pick a category from the dropdown.`,
  subcategory_parent_mismatch: (p) =>
    `The subcategory "${p.received ?? ""}" doesn't belong to the category you picked. Choose a matching subcategory.`,
  category_item_type_mismatch: () =>
    `This deal type can't be filed under the category you picked. Switch the category or change the deal type.`,
  routing_mismatch: () =>
    `We couldn't route this deal to a typed inventory. Refresh the page and try again — if it persists, tell support.`,
  discriminator_mismatch: () =>
    `The category and the deal kind don't agree. Pick the category from the dropdown so we can keep them in sync.`,
  service_block_mismatch: () =>
    `Travel-services deals need exactly one of: visa, insurance, or custom. Fill the right block for the service you're selling.`,
  invalid_currency: (p) =>
    `"${p.received ?? ""}" isn't a valid currency code. Use a 3-letter ISO code like INR, USD, or EUR.`,
  cross_group_recategorisation_required: () =>
    `You can't move a deal between categories like Stays and Flights. Create a new deal in the right category instead.`,
  promotional_category_as_primary: (p) =>
    `"${p.received ?? ""}" is a promotional badge, not a primary category. Pick a real category like Hotels or Packages.`,
  likes_not_client_settable: () =>
    `Likes are managed automatically — please don't include them when creating or updating deals.`,
  not_found: () => `That deal isn't there anymore. It may have been deleted.`,
  forbidden: () => `You don't have permission to do that on this deal.`,
};

/** Pull the structured typed-deal error from an axios-style error, if present. */
export function extractTypedDealError(err: unknown): TypedDealErrorPayload | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as {
    response?: {
      data?: {
        detail?:
          | { error?: TypedDealErrorPayload }
          | string
          | Array<{ msg?: string; type?: string }>;
      };
    };
  };
  const detail = anyErr?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const error = (detail as { error?: TypedDealErrorPayload }).error;
    if (error && typeof error === "object" && error.code) {
      return error;
    }
  }
  return null;
}

/**
 * Map a backend typed-deal error onto user-facing copy. Falls back to the
 * raw message when the code is unknown so we never lose information.
 */
export function getTypedDealErrorMessage(err: unknown): string | null {
  const payload = extractTypedDealError(err);
  if (payload) {
    const copyFn = TYPED_DEAL_ERROR_COPY[payload.code as TypedDealErrorCode];
    if (copyFn) return copyFn(payload);
    // Unknown code: surface the backend's own message rather than swallow it.
    return payload.message || `Something didn't validate (${payload.code}).`;
  }

  // Handle standard FastAPI/Pydantic validation errors (array of errors)
  const anyErr = err as {
    response?: {
      data?: {
        detail?: Array<{ msg?: string }>;
      };
    };
  };
  const detail = anyErr?.response?.data?.detail;
  if (Array.isArray(detail) && detail.length > 0 && detail[0].msg) {
    // If it's a Pydantic ValueError, the msg might start with "Value error, " so let's clean it up slightly if needed,
    // or just return it directly. Pydantic v2 often prefixes custom ValueErrors.
    return detail[0].msg.replace(/^Value error, /, "");
  }

  return null;
}

/** Convenience: returns user-facing copy for a thrown error, falling back to a default. */
export function describeDealError(err: unknown, fallback = "Something went wrong. Please try again."): string {
  return getTypedDealErrorMessage(err) ?? fallback;
}
